<div
  class="page min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-blue-100 selection:text-blue-600 flex flex-col"
  data-testid="student-dashboard-section"
>
  <!-- Fixed Header -->
  <nav class="fixed w-full z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm" data-testid="student-dashboard-navbar">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <!-- Mobile Menu Button + Logo -->
        <div class="flex items-center gap-3">
          <!-- Logo -->
          <a routerLink="/" class="flex items-center hover:opacity-80 transition-opacity" data-testid="student-dashboard-logo">
            <img src="images/logo.png" alt="JustTeach Logo" class="h-6 w-auto"/>
          </a>
        </div>

        <!-- User Info & Menu -->
        <div class="relative" data-testid="student-dashboard-user-section">
          @if (userProfile$ | async; as user) {
            <!-- Clickable User Info -->
            <button
              (click)="toggleUserMenu()"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer"
              data-testid="student-dashboard-user-menu-btn"
            >
              <div
                class="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm uppercase"
                data-testid="student-dashboard-user-avatar"
              >
                {{ (user.displayName || user.email).slice(0, 2) }}
              </div>
              <span class="text-sm text-gray-600 hidden sm:block max-w-[150px] truncate" data-testid="student-dashboard-user-email">
                {{ user.displayName || user.email }}
              </span>
              <lucide-icon
                [img]="icons.ChevronDown"
                class="w-4 h-4 text-gray-400 transition-transform"
                [class.rotate-180]="isUserMenuOpen()"
              ></lucide-icon>
            </button>

            <!-- Dropdown Menu -->
            @if (isUserMenuOpen()) {
              <!-- Backdrop to close menu -->
              <div
                class="fixed inset-0 z-40"
                (click)="closeUserMenu()"
              ></div>

              <!-- Menu -->
              <div
                class="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 animate-fade-in"
                data-testid="student-dashboard-user-dropdown"
              >
                <a
                  routerLink="/student/dashboard/profile"
                  (click)="closeUserMenu()"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition cursor-pointer"
                  data-testid="student-dashboard-profile-btn"
                >
                  <lucide-icon [img]="icons.User" class="w-4 h-4 text-gray-500"></lucide-icon>
                  <span>Profile</span>
                </a>
                <div class="border-t border-gray-100 my-1"></div>
                <button
                  (click)="logout()"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition cursor-pointer"
                  data-testid="student-dashboard-logout-btn"
                >
                  <lucide-icon [img]="icons.LogOut" class="w-4 h-4 text-gray-500"></lucide-icon>
                  <span>Logout</span>
                </button>
              </div>
            }
          }
        </div>
      </div>
    </div>
  </nav>

  <!-- Main Content with Sidebar -->
  <div class="flex-1 flex pt-16">
    <!-- Desktop Sidebar (hidden on mobile) -->
    <aside class="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-col shrink-0 fixed h-[calc(100vh-64px)] top-16">
      <!-- Navigation Tabs -->
      <nav class="flex-1 p-4">
        <ul class="space-y-1">
          @for (tab of tabs; track tab.id) {
            <li>
              <a
                [routerLink]="tab.route"
                routerLinkActive="bg-blue-100 text-blue-600 font-semibold"
                [routerLinkActiveOptions]="{ exact: true }"
                class="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer text-gray-700 hover:bg-gray-100"
              >
                <lucide-icon [img]="tab.icon" class="w-5 h-5"></lucide-icon>
                <span>{{ tab.label }}</span>
              </a>
            </li>
          }
        </ul>
      </nav>
    </aside>

    <!-- Main Content Area -->
    <main class="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 min-h-[calc(100vh-64px)] overflow-x-hidden">
      <router-outlet></router-outlet>
    </main>
  </div>

  <!-- Mobile Bottom Navigation (visible on small screens only) -->
  <nav class="lg:hidden fixed bottom-0 left-0 right-0 bg-white shadow-[0_-1px_3px_rgba(0,0,0,0.08)] z-40 safe-area-pb">
    <div class="flex items-center justify-around h-16">
      @for (tab of tabs; track tab.id) {
        <a
          [routerLink]="tab.route"
          routerLinkActive="!text-blue-600 !border-blue-600"
          [routerLinkActiveOptions]="{ exact: true }"
          class="flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors cursor-pointer text-gray-400 border-t-2 border-transparent"
        >
          <lucide-icon [img]="tab.icon" class="w-5 h-5"></lucide-icon>
          <span class="text-xs font-medium">{{ tab.label }}</span>
        </a>
      }
    </div>
  </nav>
</div>

<!-- Toast Notification -->
@if (toastState$ | async; as toast) {
  @if (toast.isVisible) {
    <div class="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-auto lg:right-6 z-[60] animate-slide-up">
      <div
        class="text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-lg flex items-center gap-3 max-w-md mx-auto lg:mx-0"
        [ngClass]="{
          'bg-green-600': toast.type === 'success',
          'bg-red-600': toast.type === 'error'
        }"
      >
        <div class="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center shrink-0">
          <lucide-icon [img]="icons.CheckCircle" class="w-5 h-5"></lucide-icon>
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-sm sm:text-base">{{ toast.message }}</p>
        </div>
        <button (click)="hideToast()" class="text-white/80 hover:text-white cursor-pointer shrink-0">
          <lucide-icon [img]="icons.X" class="w-5 h-5"></lucide-icon>
        </button>
      </div>
    </div>
  }
}
